/**
 * reviews-widget.js — Sprint 1 stub
 * ReviewAggregate + ReviewList web components.
 * No photos, no Turnstile. HMAC config from window.reviewConfig.
 */

const cfg = () => window.reviewConfig || {};
const t   = key => window.reviewTranslations?.[key] ?? key;

// ── Utilities ────────────────────────────────────────────────

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

async function apiFetch(path, options = {}) {
  const base = cfg().apiBase || 'https://reviews.3eeed.app';
  const res  = await fetch(`${base}${path}`, options);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

function showToast(message, type = '') {
  let container = document.querySelector('.review-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.className = 'review-toast-container';
    const pos = cfg().toastPosition || 'bottom-center';
    if (pos === 'bottom-right')  { container.style.cssText += 'left:auto;right:1.5rem;transform:none;'; }
    else if (pos === 'bottom-left')  { container.style.cssText += 'left:1.5rem;transform:none;'; }
    else if (pos === 'top-center')   { container.style.cssText += 'bottom:auto;top:1.5rem;'; }
    document.body.appendChild(container);
  }
  const toast = document.createElement('div');
  toast.className = `review-toast${type ? ` review-toast--${type}` : ''}`;
  toast.textContent = message;
  if (!type) {
    if (cfg().toastBg)   toast.style.background = cfg().toastBg;
    if (cfg().toastText) toast.style.color = cfg().toastText;
  }
  container.appendChild(toast);
  setTimeout(() => toast.remove(), 3000);
}

function starsSVG(rating) {
  return Array.from({ length: 5 }, (_, i) => {
    const filled = i < Math.round(rating);
    return `<svg class="review-stars__star${filled ? ' review-stars__star--filled' : ''}"
      viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>`;
  }).join('');
}

const THUMB_SVG        = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex-shrink:0;vertical-align:text-bottom;"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>`;
const THUMB_SVG_ACTIVE = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" style="flex-shrink:0;vertical-align:text-bottom;"><path d="M6.956 1.745C7.021.81 7.908.087 8.864.325l.261.066c.463.116.874.456 1.012.965.22.816.533 2.511.062 4.51a9.84 9.84 0 0 1 .443-.051c.713-.065 1.669-.072 2.516.21.518.173.994.681 1.2 1.273.184.532.16 1.162-.234 1.733.058.119.103.242.138.363.077.27.113.567.113.856 0 .289-.036.586-.113.856-.039.135-.09.273-.16.404.169.387.107.819-.003 1.148a3.163 3.163 0 0 1-.488.901c.054.152.076.312.076.465 0 .305-.089.625-.253.912C13.1 15.522 12.437 16 11.5 16H8c-.605 0-1.07-.081-1.466-.218a4.82 4.82 0 0 1-.97-.484l-.048-.03c-.504-.307-.999-.609-2.068-.722C2.682 14.464 2 13.846 2 13V9c0-.85.685-1.432 1.357-1.615.849-.232 1.574-.787 2.132-1.41.56-.627.914-1.28 1.039-1.639.199-.575.356-1.539.428-2.59z"/></svg>`;

// ── localStorage: helpful votes ──────────────────────────────

class ReviewVotes {
  static #KEY = 'padelneeds_review_votes';
  static get(id)  { try { return JSON.parse(localStorage.getItem(this.#KEY) || '{}')[id] ?? false; } catch { return false; } }
  static add(id)  { try { const v = JSON.parse(localStorage.getItem(this.#KEY) || '{}'); v[id] = true; localStorage.setItem(this.#KEY, JSON.stringify(v)); } catch {} }
}

// ── ReviewAggregate web component ───────────────────────────

class ReviewAggregate extends HTMLElement {
  connectedCallback() {
    this.#load();
  }

  async #load() {
    const productId = this.dataset.productId || cfg().productId;
    try {
      const data = await apiFetch(`/api/reviews/aggregate?product_id=${productId}`);
      this.#render(data);
    } catch {
      this.innerHTML = `<p role="alert" class="review-aggregate__error">${escapeHtml(t('error_loading'))}</p>`;
    }
  }

  #render({ avg_rating = 0, review_count = 0, distribution = {} }) {
    if (review_count === 0) {
      this.innerHTML = '';
      return;
    }

    const avgDisplay = Number(avg_rating).toFixed(1);
    const bars = [5, 4, 3, 2, 1].map(star => {
      const count = distribution[star] ?? 0;
      const pct   = Math.round((count / review_count) * 100);
      return `<div class="review-aggregate__row">
        <span class="review-aggregate__row-label">${star}★</span>
        <div class="review-aggregate__bar-bg">
          <div class="review-aggregate__bar-fill" style="width:${pct}%" role="presentation"></div>
        </div>
        <span class="review-aggregate__row-count">${count}</span>
      </div>`;
    }).join('');

    this.innerHTML = `
      <div class="review-aggregate">
        <div class="review-aggregate__summary">
          <span class="review-aggregate__avg" aria-label="${avgDisplay} ${t('stars')}">${avgDisplay}</span>
          <div class="review-stars" dir="ltr">${starsSVG(avg_rating)}</div>
          <span class="review-aggregate__count">${review_count} ${review_count === 1 ? t('review_count_one') : t('review_count_other')}</span>
        </div>
        <div class="review-aggregate__histogram" aria-label="${escapeHtml(t('aggregate_label'))}">${bars}</div>
        <button class="review-aggregate__write-cta" data-review-write
                aria-label="${escapeHtml(t('write_a_review'))}">
          ${escapeHtml(t('write_a_review'))}
        </button>
      </div>`;
  }
}

customElements.define('review-aggregate', ReviewAggregate);

// ── ReviewList web component ─────────────────────────────────

class ReviewList extends HTMLElement {
  #page   = 1;
  #sort   = 'recent';
  #total  = 0;

  connectedCallback() {
    this.#sort = this.dataset.sort || cfg().defaultSort || 'recent';
    this.#loadPage(1);
  }

  #renderControls() {
    const sortOpts = ['recent', 'helpful', 'rating_high', 'rating_low'].map(v =>
      `<option value="${v}"${v === this.#sort ? ' selected' : ''}>${escapeHtml(t(`sort_${v}`))}</option>`
    ).join('');

    const controls = document.createElement('div');
    controls.className = 'product-reviews__controls';
    controls.innerHTML = `
      <select class="product-reviews__sort" data-review-sort aria-label="${escapeHtml(t('sort_label') || 'Sort by')}">
        ${sortOpts}
      </select>`;
    this.prepend(controls);
  }

  async #loadPage(page) {
    const productId = this.dataset.productId || cfg().productId;
    const perPage   = this.dataset.perPage   || cfg().perPage || 10;
    const offset    = (page - 1) * perPage;
    const params    = new URLSearchParams({ product_id: productId, offset, limit: perPage, sort: this.#sort });

    try {
      const { reviews, total } = await apiFetch(`/api/reviews/list?${params}`);
      this.#total = total;
      this.#renderPage(reviews, page, total, perPage);
    } catch {
      const err = document.createElement('p');
      err.role = 'alert';
      err.className = 'review-aggregate__error';
      err.textContent = t('error_loading');
      this.querySelector('.review-skeleton--list, .review-skeleton')?.replaceWith(err) || this.appendChild(err);
    }
  }

  #renderPage(reviews, page, total, perPage) {
    this.querySelector('.review-skeleton--list, .review-skeleton')?.remove();
    this.querySelector('.product-reviews__load-more')?.remove();

    if (reviews.length === 0 && page === 1) {
      // No reviews → no sort controls, just the hero CTA
      this.querySelector('.product-reviews__controls')?.remove();
      this.insertAdjacentHTML('beforeend', `
        <div class="review-empty-state review-empty-state--hero" role="status">
          <svg class="review-empty-state__icon" viewBox="0 0 56 56" fill="none" aria-hidden="true">
            <circle cx="28" cy="28" r="28" fill="currentColor" opacity="0.08"/>
            <path d="M28 14l4.12 8.35L42 23.63l-7 6.82 1.65 9.62L28 35.42l-8.65 4.55L21 30.45l-7-6.82 9.88-1.28L28 14z"
                  fill="currentColor" opacity="0.5"/>
          </svg>
          <p class="review-empty-state__title">${escapeHtml(t('empty_title'))}</p>
          <p class="review-empty-state__subtitle">${escapeHtml(t('empty_message'))}</p>
          <button class="review-empty-state__cta" data-review-write>${escapeHtml(t('empty_cta'))}</button>
        </div>`);
      return;
    }

    // Render sort controls lazily on first page with reviews
    if (page === 1 && !this.querySelector('.product-reviews__controls')) {
      this.#renderControls();
    }

    reviews.forEach(r => {
      this.insertAdjacentHTML('beforeend', this.#cardHTML(r));
    });

    this.#page = page;
    const loaded = page * perPage;
    if (loaded < total) {
      this.insertAdjacentHTML('beforeend',
        `<button class="product-reviews__load-more" data-review-load-more data-next-page="${page + 1}">
          ${escapeHtml(t('load_more'))}
        </button>`);
    }
  }

  #cardHTML(r) {
    // Backend sends Unix timestamps in seconds; JS Date wants milliseconds.
    const ts = r.approved_at ?? r.created_at;
    const date = ts ? new Date(ts * 1000).toLocaleDateString() : '';
    const verified = (r.verified_buyer ?? r.verified)
      ? `<span class="review-card__verified">✓ ${escapeHtml(t('verified_buyer'))}</span>` : '';
    const alreadyVoted = ReviewVotes.get(r.id);
    const helpfulCount = r.helpful_count ?? 0;
    const author = r.customer_name || r.author_name || 'Anonymous';

    const initial = (author || 'A').trim().charAt(0).toUpperCase();
    const apiBase = cfg().apiBase || 'https://reviews.3eeed.app';
    const photos = Array.isArray(r.photos) ? r.photos : [];
    const photosHTML = photos.length ? `
      <div class="review-card__photos">
        ${photos.map(p => {
          // Backend returns relative URLs like /api/reviews/photo/.../filename.jpg
          const url = (p.url && p.url.startsWith('http')) ? p.url : `${apiBase}${p.url || ''}`;
          const thumb = (p.thumb_url && p.thumb_url.startsWith('http')) ? p.thumb_url
                       : p.thumb_url ? `${apiBase}${p.thumb_url}` : url;
          return `<button class="review-card__photo" type="button"
                          data-review-photo="${escapeHtml(url)}"
                          aria-label="${escapeHtml(t('photo_open') || 'Open photo')}">
            <img src="${escapeHtml(thumb)}" alt="" loading="lazy">
          </button>`;
        }).join('')}
      </div>` : '';

    const replies = (cfg().showOwnerReply !== false && Array.isArray(r.replies) && r.replies.length)
      ? r.replies.map(rep => {
          const repDate = rep.created_at ? new Date(rep.created_at * 1000).toLocaleDateString() : '';
          return `<div class="review-card__reply">
            <div class="review-card__reply-header">
              <span class="review-card__reply-label">${escapeHtml(t('owner_reply') || 'Owner Reply')}</span>
              ${repDate ? `<time class="review-card__reply-date">${escapeHtml(repDate)}</time>` : ''}
            </div>
            <p class="review-card__reply-body">${escapeHtml(rep.body || '')}</p>
          </div>`;
        }).join('')
      : '';

    return `<div class="review-card" id="review-${escapeHtml(r.id)}" data-author-initial="${escapeHtml(initial)}">
      <div class="review-card__header">
        <div class="review-card__meta">
          <span class="review-card__author">${escapeHtml(author)}</span>
          <div class="review-stars" dir="ltr">${starsSVG(r.rating)}</div>
          ${verified}
          <time class="review-card__date" datetime="${escapeHtml(String(ts || ''))}">${escapeHtml(date)}</time>
        </div>
      </div>
      ${r.title ? `<p class="review-card__title">${escapeHtml(r.title)}</p>` : ''}
      <p class="review-card__body">${escapeHtml(r.body || '')}</p>
      ${photosHTML}
      ${replies}
      <button
        class="review-helpful__button${alreadyVoted ? ' is-active' : ''}"
        data-review-helpful="${escapeHtml(r.id)}"
        aria-pressed="${alreadyVoted}"
        aria-label="${escapeHtml(t('helpful_question'))}">
        ${alreadyVoted ? THUMB_SVG_ACTIVE : THUMB_SVG} ${helpfulCount}
      </button>
    </div>`;
  }

  applySort(sort) {
    this.#sort = sort;
    this.querySelectorAll('.review-card, .review-empty-state, .product-reviews__load-more').forEach(el => el.remove());
    this.insertAdjacentHTML('beforeend', `
      <div class="review-skeleton review-skeleton--list">
        <div class="review-skeleton__card"></div>
        <div class="review-skeleton__card"></div>
      </div>`);
    this.#loadPage(1);
  }

  loadMore(page) {
    this.#loadPage(page);
  }
}

customElements.define('review-list', ReviewList);

// ── Event delegation ─────────────────────────────────────────

document.addEventListener('click', async e => {
  // Write review CTA — open form (works for guests + logged-in users)
  if (e.target.closest('[data-review-write]')) {
    const form = document.querySelector('review-form');
    if (form?.open) {
      form.open();
    } else {
      showToast(t('error_loading'));
    }
    return;
  }

  // Helpful vote
  const helpfulBtn = e.target.closest('[data-review-helpful]');
  if (helpfulBtn) {
    const reviewId = helpfulBtn.dataset.reviewHelpful;
    if (ReviewVotes.get(reviewId)) {
      showToast(t('helpful_already_voted'));
      return;
    }
    const countMatch = helpfulBtn.textContent.match(/\d+/);
    const prevCount  = countMatch ? parseInt(countMatch[0], 10) : 0;
    helpfulBtn.setAttribute('aria-pressed', 'true');
    helpfulBtn.classList.add('is-active');
    helpfulBtn.innerHTML = `${THUMB_SVG_ACTIVE} ${prevCount + 1}`;
    try {
      await apiFetch(`/api/reviews/${reviewId}/helpful`, { method: 'POST' });
      ReviewVotes.add(reviewId);
      showToast(t('helpful_thanks'), 'success');
    } catch {
      helpfulBtn.setAttribute('aria-pressed', 'false');
      helpfulBtn.classList.remove('is-active');
      helpfulBtn.innerHTML = `${THUMB_SVG} ${prevCount}`;
      showToast(t('error_loading'), 'error');
    }
    return;
  }

  // Load more
  const loadMoreBtn = e.target.closest('[data-review-load-more]');
  if (loadMoreBtn) {
    const nextPage = parseInt(loadMoreBtn.dataset.nextPage, 10);
    const list     = loadMoreBtn.closest('review-list');
    if (list) list.loadMore(nextPage);
    return;
  }

  // Photo lightbox — open full-size photo in overlay
  const photoBtn = e.target.closest('[data-review-photo]');
  if (photoBtn) {
    const url = photoBtn.dataset.reviewPhoto;
    if (url) openPhotoLightbox(url);
    return;
  }
});

function openPhotoLightbox(url) {
  const overlay = document.createElement('div');
  overlay.className = 'review-photo-lightbox';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.innerHTML = `
    <button class="review-photo-lightbox__close" aria-label="Close" type="button">✕</button>
    <img class="review-photo-lightbox__img" src="${url.replace(/"/g, '&quot;')}" alt="">`;
  document.body.appendChild(overlay);
  document.body.style.overflow = 'hidden';
  const close = () => { overlay.remove(); document.body.style.overflow = ''; document.removeEventListener('keydown', onKey); };
  const onKey = e => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKey);
  overlay.addEventListener('click', e => {
    if (e.target === overlay || e.target.closest('.review-photo-lightbox__close')) close();
  });
}

document.addEventListener('change', e => {
  if (e.target.matches('[data-review-sort]')) {
    const list = e.target.closest('review-list');
    if (list) list.applySort(e.target.value);
  }
});
